// Where this tab is in the store: the cursor the poll sends as `?since&epoch`.
//
// A module, not StoreSync's state, because three places touch it: StoreSync
// reads and moves it, store-persist asks for a full resync when the tab has
// drifted (forget's failure), and the seed sets it on the first
// document load. It imports nothing but the groups' names, so none of them
// closes a cycle.
//
// ─── AND WHICH GROUPS THE CURSOR IS FOR ─────────────────────────────────────
//
// A tab no longer holds the whole store: it holds the groups the pages it has
// shown read (lib/route-store-groups.ts), and its ONE cursor is the seq all of
// them are current to. A group is HELD once a whole dump of it has been applied
// — the document's seed, a group load, a full answer — and never let go: the
// diff keeps it current from then on, and a page that reads it renders at
// once. A group is WANTED when a page about to render reads it and the tab does
// not hold it; StoreSync's next poll loads it beside the diff, and the page
// waits for it (components/store-group-gate.tsx), so a detail page never
// decides from an empty store that its script does not exist.

import { STORE_GROUPS, type StoreGroup } from "./store-groups";

/** The server's answer this tab is current to: the epoch the seq was counted in, and the seq. */
export interface StoreCursor { epoch: string; seq: number }

let cursor: StoreCursor | null = null;
let fullNext = false;

export function storeCursor(): StoreCursor | null {
  return cursor;
}

export function setStoreCursor(next: StoreCursor | null): void {
  cursor = next;
}

/**
 * Ask the next poll for the whole store instead of a diff.
 *
 * FOR A TAB THAT KNOWS IT HAS DRIFTED with nothing pending to say so. A diff
 * only carries what the server CHANGED, and a refused DELETE changed nothing:
 * the tab dropped the row, the server kept it, and no seq will ever bring it
 * back. Before the diff every poll re-sent every row and healed that on the
 * next tick (lib/same-data.ts); this is that heal, asked for once.
 */
export function resyncStoreNext(): void {
  fullNext = true;
}

/** True once per resyncStoreNext(): the poll that takes it sends no cursor. */
export function takeResync(): boolean {
  const was = fullNext;
  fullNext = false;
  return was;
}

const held = new Set<StoreGroup>();
const wanted = new Set<StoreGroup>();
const groupListeners = new Set<() => void>();
let groupsVersion = 0;

/** The groups this tab holds: a whole dump of each has been applied, and the diff keeps it current. */
export function heldGroups(): StoreGroup[] {
  return STORE_GROUPS.filter((g) => held.has(g));
}

/** True when the tab holds every one of these groups (and so for none). */
export function holdsGroups(groups: readonly StoreGroup[]): boolean {
  return groups.every((g) => held.has(g));
}

/** A whole dump of these groups has been applied (lib/store-hydrate.ts). */
export function markGroupsHeld(groups: readonly StoreGroup[]): void {
  let moved = false;
  for (const g of groups) {
    if (held.has(g)) continue;
    held.add(g);
    moved = true;
  }
  if (!moved) return;
  bumpGroups();
}

/** A page about to render reads these. True when one is not held, so a poll is owed. */
export function wantGroups(groups: readonly StoreGroup[]): boolean {
  let missing = false;
  for (const g of groups) {
    if (held.has(g)) continue;
    wanted.add(g);
    missing = true;
  }
  return missing;
}

/** The groups a page has asked for that the tab does not hold yet: the next poll's `load`. A held group is never wanted again. */
export function wantedGroups(): StoreGroup[] {
  return STORE_GROUPS.filter((g) => wanted.has(g) && !held.has(g));
}

// ─── AND WHETHER THE LAST LOAD FAILED ───────────────────────────────────────
//
// A page waiting on a group shows the gate's "Loading…". When the poll carrying
// that load fails, a page that only kept saying "Loading…" would be a frozen
// screen, and nothing may look hung, so the gate says it did not load,
// with Try again. Cleared the moment a load goes out again, so the interval's
// own retry reads as loading too (and so a load that lands was cleared as it
// went out).

let loadFailed = false;

function bumpGroups(): void {
  groupsVersion++;
  groupListeners.forEach((l) => l());
}

/** True when the last poll that carried a group load failed, and none has gone out since. */
export function groupLoadFailed(): boolean {
  return loadFailed;
}

/** StoreSync's word on a poll that carries a load: going out, or failed. */
export function noteGroupLoad(state: "sent" | "failed"): void {
  const failed = state === "failed";
  if (loadFailed === failed) return;
  loadFailed = failed;
  bumpGroups();
}

export function subscribeGroups(listener: () => void): () => void {
  groupListeners.add(listener);
  return () => {
    groupListeners.delete(listener);
  };
}

export function getGroupsVersion(): number {
  return groupsVersion;
}

// ─── AND A WAY TO ASK NOW ────────────────────────────────────────────────────
//
// StoreSync owns the poll and its schedule. One reader needs a poll sooner than
// the next tick: a script editor reloaded with its own save still on the way
// (lib/editor-own-save.ts) asks once that save could have landed, rather than
// show the old text for up to 10 s. StoreSync registers its poll here; with no
// StoreSync mounted (a unit test, a page outside the layout) the ask is a no-op.

let pollNow: (() => Promise<void>) | null = null;

/** StoreSync's registration. Returns the unregister, which only clears its own. */
export function setStorePoll(fn: () => Promise<void>): () => void {
  pollNow = fn;
  return () => {
    if (pollNow === fn) pollNow = null;
  };
}

/** A poll that starts now, after any already in flight. Never rejects. */
export function pollStoreNow(): Promise<void> {
  return pollNow ? pollNow().catch(() => undefined) : Promise.resolve();
}

/** A fresh tab, for a test: no cursor, no resync owed, no group held or wanted. */
export function resetTabForTests(): void {
  cursor = null;
  fullNext = false;
  held.clear();
  wanted.clear();
  loadFailed = false;
  groupsVersion++;
}
